import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Avatar,
    IconButton,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Snackbar,
    Skeleton,
    Chip,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Person as PersonIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import {
    getAllUsersForBusiness,
    addProjectMembers,
    removeProjectMember,
    listProjectMembers,
} from '../../services/projectDashboardService';

/**
 * Enhanced Search Component
 */
const SearchBar = ({ searchTerm, onSearchChange, onClear }) => {
    return (
        <TextField
            fullWidth
            placeholder="Search users by name, email, or business name..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            size="small"
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#64748b', fontSize: 20 }} />
                    </InputAdornment>
                ),
                endAdornment: searchTerm && (
                    <InputAdornment position="end">
                        <IconButton onClick={onClear} size="small" sx={{ p: 0.5 }}>
                            <ClearIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e5e7eb',
                    '&:hover': {
                        borderColor: '#d1d5db',
                    },
                    '&.Mui-focused': {
                        borderColor: '#1652f0',
                        backgroundColor: '#fff',
                    },
                },
            }}
        />
    );
};

/**
 * Loading Skeleton Component for User Cards in Modal
 */
const UserSkeleton = () => {
    return (
        <Card
            sx={{
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: 'none',
                mb: 2,
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flexGrow: 1 }}>
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton variant="text" width="80%" height={20} />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

/**
 * Add User Modal Component
 */
const AddUserModal = ({ open, onClose, onUserAdded, projectId, existingMemberUserIds = [] }) => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState(null);

    // Load users when modal opens
    useEffect(() => {
        if (open) {
            loadUsers();
        } else {
            // Reset state when modal closes
            setUsers([]);
            setFilteredUsers([]);
            setSearchTerm('');
            setSelectedUserIds(new Set());
            setError(null);
        }
    }, [open]);

    // Filter users based on search term
    useEffect(() => {
        const search = searchTerm.trim().toLowerCase();
        const existingUserIds = new Set(existingMemberUserIds);

        const availableUsers = users.filter((userObj) => !existingUserIds.has(userObj.user.userId));
        const filtered = availableUsers.filter((userObj) => {
            if (!search) return true;
            const fullName = `${userObj.user.firstName || ''} ${userObj.user.lastName || ''}`;
            return (
                fullName.toLowerCase().includes(search) ||
                (userObj.user.email || '').toLowerCase().includes(search) ||
                (userObj.business.businessName || '').toLowerCase().includes(search)
            );
        });
        setFilteredUsers(filtered);
    }, [users, searchTerm, existingMemberUserIds]);

    const selectedUsers = users.filter((userObj) => selectedUserIds.has(userObj.user.userId));

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const userData = await getAllUsersForBusiness();
            setUsers(userData);
        } catch (err) {
            console.error('Error loading users:', err);
            setError('Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async () => {
        if (selectedUsers.length === 0) return;

        try {
            setAdding(true);
            setError(null);

            const membershipsData = selectedUsers.map((userObj) => ({
                projectId: projectId,
                userId: userObj.user.userId,
                role: userObj.role,
            }));

            const createdMemberships = await addProjectMembers(projectId, membershipsData);

            onUserAdded(createdMemberships);
            onClose();
        } catch (err) {
            console.error('Error adding users to project:', err);

            const message = err?.response?.data || err?.message || '';

            if (message.toLowerCase().includes('already')) {
                setError('One or more selected users are already a part of this project.');
            } else {
                setError('Failed to add users to project. Please try again.');
            }
        } finally {
            setAdding(false);
        }
    };

    const handleSearchClear = () => {
        setSearchTerm('');
    };

    const handleUserToggle = (userId) => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    const getInitials = (firstName, lastName) => {
        return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    };

    const formatRoleDisplay = (role) => {
        if (!role) return 'No Role';
        return role
            .replace(/[_]/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (l) => l.toUpperCase());
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Add User to Project
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Search Bar */}
                <Box sx={{ mb: 3 }}>
                    <SearchBar
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        onClear={handleSearchClear}
                    />
                </Box>

                {/* Users List */}
                <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {loading ? (
                        // Loading skeletons
                        [...Array(3)].map((_, index) => <UserSkeleton key={index} />)
                    ) : filteredUsers.length === 0 ? (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 4,
                                color: 'text.secondary',
                            }}
                        >
                            <PersonIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                            <Typography variant="body1">
                                {searchTerm
                                    ? 'No users found matching your search.'
                                    : 'All available users are already on this project.'}
                            </Typography>
                        </Box>
                    ) : (
                        filteredUsers.map((userObj) => {
                            const userId = userObj.user.userId;
                            const isSelected = selectedUserIds.has(userId);

                            return (
                                <Card
                                    key={userId}
                                    sx={{
                                        mb: 1,
                                        cursor: 'pointer',
                                        border: '1px solid',
                                        borderColor: isSelected ? '#1652f0' : '#e5e7eb',
                                        backgroundColor: isSelected ? '#f0f7ff' : '#fff',
                                        '&:hover': {
                                            borderColor: '#1652f0',
                                            backgroundColor: '#f8fafc',
                                        },
                                    }}
                                    onClick={() => handleUserToggle(userId)}
                                >
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Checkbox
                                                checked={isSelected}
                                                tabIndex={-1}
                                                disableRipple
                                                inputProps={{
                                                    'aria-label': `Select ${userObj.user.firstName} ${userObj.user.lastName}`,
                                                }}
                                                sx={{ p: 0 }}
                                            />
                                            <Avatar
                                                sx={{
                                                    bgcolor: '#1652f0',
                                                    width: 40,
                                                    height: 40,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {getInitials(
                                                    userObj.user.firstName,
                                                    userObj.user.lastName
                                                )}
                                            </Avatar>
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {userObj.user.firstName} {userObj.user.lastName}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mb: 0.5,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {userObj.user.email}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 1,
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Chip
                                                        label={formatRoleDisplay(userObj.role)}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: userObj.isInternalUser
                                                                ? '#e3f2fd'
                                                                : '#f3e5f5',
                                                            color: userObj.isInternalUser
                                                                ? '#1565c0'
                                                                : '#7b1fa2',
                                                            fontWeight: 500,
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {userObj.business.businessName}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            );
                        })
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 2, justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                    {selectedUsers.length} selected
                </Typography>
                <Box>
                    <Button onClick={onClose} sx={{ mr: 1 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddUser}
                        disabled={selectedUsers.length === 0 || adding}
                        sx={{
                            backgroundColor: '#1652f0',
                            '&:hover': { backgroundColor: '#1441cc' },
                            minWidth: 120,
                        }}
                    >
                        {adding ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : selectedUsers.length > 1 ? (
                            `Add ${selectedUsers.length} Users`
                        ) : (
                            'Add User'
                        )}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Main ManageUsers Component
 */
const ManageUsers = ({ projectId, canWriteMembers = false }) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Snackbar state for notifications
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    // Load project members on component mount
    useEffect(() => {
        if (projectId) {
            loadProjectMembers();
        }
    }, [projectId]);

    const loadProjectMembers = async () => {
        try {
            setLoading(true);
            setError(null);
            const memberData = await listProjectMembers(projectId);
            setMembers(memberData);
        } catch (err) {
            console.error('Error loading project members:', err);
            setError('Failed to load project members. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveUser = async (user) => {
        if (
            !window.confirm(
                `Are you sure you want to remove ${user.firstName} ${user.lastName} from this project?`
            )
        ) {
            return;
        }

        try {
            await removeProjectMember(user.membershipId);
            setSnackbar({
                open: true,
                message: `${user.firstName} ${user.lastName} has been removed from the project.`,
                severity: 'success',
            });
            loadProjectMembers(); // Refresh the list
        } catch (err) {
            console.error('Error removing user:', err);
            setSnackbar({
                open: true,
                message: 'Failed to remove user. Please try again.',
                severity: 'error',
            });
        }
    };

    const handleUserAdded = (createdMemberships) => {
        setSearchTerm('');
        const membershipsToAdd = Array.isArray(createdMemberships)
            ? createdMemberships
            : [createdMemberships].filter(Boolean);

        if (membershipsToAdd.length > 0) {
            setMembers((prev) => {
                const existingMembershipIds = new Set(prev.map((member) => member.membershipId));
                const newMemberships = membershipsToAdd.filter(
                    (membership) => !existingMembershipIds.has(membership.membershipId)
                );
                return [...prev, ...newMemberships];
            });
        }
        setSnackbar({
            open: true,
            message:
                membershipsToAdd.length > 1
                    ? `${membershipsToAdd.length} users have been successfully added to the project.`
                    : 'User has been successfully added to the project.',
            severity: 'success',
        });
        loadProjectMembers(); // Refresh the list
    };

    const handleSearchClear = () => {
        setSearchTerm('');
    };

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // Filter members based on search term
    const filteredMembers = members.filter((member) => {
        if (!searchTerm.trim()) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchLower) ||
            member.email.toLowerCase().includes(searchLower)
        );
    });

    if (loading) {
        return (
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
                <Grid2 container spacing={0} sx={{ width: '100%', margin: 0 }}>
                    <Grid2 size={{ xs: 12 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 3,
                                px: { xs: 2, sm: 0 },
                            }}
                        >
                            <Skeleton variant="text" width={200} height={32} />
                            {canWriteMembers && (
                                <Skeleton variant="rectangular" width={120} height={36} />
                            )}
                        </Box>
                        <Box sx={{ px: { xs: 2, sm: 0 }, mb: 3 }}>
                            <Skeleton variant="rectangular" width="100%" height={48} />
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ px: { xs: 2, sm: 0 } }}>
                            <TableContainer
                                component={Paper}
                                sx={{
                                    borderRadius: '12px',
                                    overflow: 'auto',
                                    width: '100%',
                                    maxWidth: '100%',
                                }}
                            >
                                <Table sx={{ minWidth: 300, width: '100%' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#0f172a',
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                    py: { xs: 1.5, sm: 2 },
                                                }}
                                            >
                                                Name
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#0f172a',
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                    display: { xs: 'none', sm: 'table-cell' },
                                                    py: { xs: 1.5, sm: 2 },
                                                }}
                                            >
                                                Email
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#0f172a',
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                    display: { xs: 'none', md: 'table-cell' },
                                                    py: { xs: 1.5, sm: 2 },
                                                }}
                                            >
                                                Role
                                            </TableCell>
                                            {canWriteMembers && (
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#0f172a',
                                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                        py: { xs: 1.5, sm: 2 },
                                                        width: { xs: 60, sm: 80 },
                                                    }}
                                                    align="right"
                                                >
                                                    Actions
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[...Array(3)].map((_, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: { xs: 1, sm: 1.5 },
                                                        }}
                                                    >
                                                        <Skeleton
                                                            variant="circular"
                                                            width={{ xs: 28, sm: 32 }}
                                                            height={{ xs: 28, sm: 32 }}
                                                        />
                                                        <Skeleton
                                                            variant="text"
                                                            width={120}
                                                            height={20}
                                                        />
                                                    </Box>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        display: { xs: 'none', sm: 'table-cell' },
                                                        py: { xs: 1.5, sm: 2 },
                                                    }}
                                                >
                                                    <Skeleton
                                                        variant="text"
                                                        width={180}
                                                        height={20}
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        display: { xs: 'none', md: 'table-cell' },
                                                        py: { xs: 1.5, sm: 2 },
                                                    }}
                                                >
                                                    <Skeleton
                                                        variant="rectangular"
                                                        width={80}
                                                        height={24}
                                                        sx={{ borderRadius: '12px' }}
                                                    />
                                                </TableCell>
                                                {canWriteMembers && (
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            py: { xs: 1.5, sm: 2 },
                                                            width: { xs: 60, sm: 80 },
                                                        }}
                                                    >
                                                        <Skeleton
                                                            variant="circular"
                                                            width={{ xs: 24, sm: 32 }}
                                                            height={{ xs: 24, sm: 32 }}
                                                        />
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Grid2>
                </Grid2>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Alert severity="error" sx={{ borderRadius: '8px' }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
            <Grid2 container spacing={0} sx={{ width: '100%', margin: 0 }}>
                {/* Header Section */}
                <Grid2 size={{ xs: 12 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            gap: { xs: 2, sm: 0 },
                            mb: 3,
                            px: { xs: 2, sm: 0 },
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: '#0f172a',
                                fontSize: { xs: '1.1rem', sm: '1.5rem' },
                            }}
                        >
                            Project Members ({members.length})
                        </Typography>

                        {canWriteMembers && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setAddModalOpen(true)}
                                sx={{
                                    backgroundColor: '#1652f0',
                                    '&:hover': { backgroundColor: '#1441cc' },
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderRadius: '8px',
                                    px: { xs: 2, sm: 3 },
                                    py: 1,
                                    boxShadow: '0 2px 8px rgba(22, 82, 240, 0.2)',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                }}
                            >
                                Add User
                            </Button>
                        )}
                    </Box>
                </Grid2>

                {/* Search Bar */}
                <Grid2 size={{ xs: 12 }}>
                    <Box sx={{ px: { xs: 2, sm: 0 }, mb: 3 }}>
                        <SearchBar
                            searchTerm={searchTerm}
                            onSearchChange={setSearchTerm}
                            onClear={handleSearchClear}
                        />
                    </Box>
                </Grid2>

                {/* Members List */}
                <Grid2 size={{ xs: 12 }}>
                    {filteredMembers.length === 0 ? (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 6,
                                mx: { xs: 2, sm: 0 },
                                color: 'text.secondary',
                                backgroundColor: '#fafafa',
                                borderRadius: '12px',
                                border: '1px dashed #e0e0e0',
                            }}
                        >
                            <PersonIcon
                                sx={{ fontSize: { xs: 48, sm: 64 }, mb: 2, opacity: 0.5 }}
                            />
                            <Typography
                                variant="h6"
                                sx={{ mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}
                            >
                                {searchTerm ? 'No users found' : 'No project members yet'}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                                {searchTerm
                                    ? 'Try adjusting your search terms.'
                                    : canWriteMembers
                                      ? 'Click "Add User" to start adding team members to this project.'
                                      : 'No project members have been added yet.'}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ px: { xs: 2, sm: 0 } }}>
                            <TableContainer
                                component={Paper}
                                sx={{
                                    borderRadius: '12px',
                                    overflow: 'auto',
                                    width: '100%',
                                    maxWidth: '100%',
                                }}
                            >
                                <Table sx={{ minWidth: 300, width: '100%' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#0f172a',
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                    py: { xs: 1.5, sm: 2 },
                                                }}
                                            >
                                                Name
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#0f172a',
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                    display: { xs: 'none', sm: 'table-cell' },
                                                    py: { xs: 1.5, sm: 2 },
                                                }}
                                            >
                                                Email
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#0f172a',
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                    display: { xs: 'none', md: 'table-cell' },
                                                    py: { xs: 1.5, sm: 2 },
                                                }}
                                            >
                                                Role
                                            </TableCell>
                                            {canWriteMembers && (
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#0f172a',
                                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                        py: { xs: 1.5, sm: 2 },
                                                        width: { xs: 60, sm: 80 },
                                                    }}
                                                    align="right"
                                                >
                                                    Actions
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredMembers.map((member) => (
                                            <TableRow
                                                key={member.membershipId}
                                                sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}
                                            >
                                                <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: { xs: 1, sm: 1.5 },
                                                        }}
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: '#1652f0',
                                                                width: { xs: 28, sm: 32 },
                                                                height: { xs: 28, sm: 32 },
                                                                fontSize: {
                                                                    xs: '0.7rem',
                                                                    sm: '0.875rem',
                                                                },
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {member.firstName?.charAt(0) || ''}
                                                            {member.lastName?.charAt(0) || ''}
                                                        </Avatar>
                                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: '#0f172a',
                                                                    fontSize: {
                                                                        xs: '0.8rem',
                                                                        sm: '0.875rem',
                                                                    },
                                                                    lineHeight: 1.3,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {member.firstName} {member.lastName}
                                                            </Typography>
                                                            {/* Show email on mobile under the name */}
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display: {
                                                                        xs: 'block',
                                                                        sm: 'none',
                                                                    },
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    fontSize: '0.7rem',
                                                                    lineHeight: 1.2,
                                                                    mt: 0.25,
                                                                }}
                                                            >
                                                                {member.email}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        display: { xs: 'none', sm: 'table-cell' },
                                                        py: { xs: 1.5, sm: 2 },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontSize: {
                                                                xs: '0.8rem',
                                                                sm: '0.875rem',
                                                            },
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: 200,
                                                        }}
                                                    >
                                                        {member.email}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        display: { xs: 'none', md: 'table-cell' },
                                                        py: { xs: 1.5, sm: 2 },
                                                    }}
                                                >
                                                    <Chip
                                                        label={
                                                            member.role
                                                                ? member.role
                                                                      .replace(/[_]/g, ' ')
                                                                      .toLowerCase()
                                                                      .replace(/\b\w/g, (l) =>
                                                                          l.toUpperCase()
                                                                      )
                                                                : 'No Role'
                                                        }
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#f1f5f9',
                                                            color: '#475569',
                                                            fontSize: '0.7rem',
                                                            fontWeight: 500,
                                                            height: 'auto',
                                                            maxWidth: 120,
                                                            '& .MuiChip-label': {
                                                                px: 1,
                                                                py: 0.5,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                {canWriteMembers && (
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            py: { xs: 1.5, sm: 2 },
                                                            width: { xs: 60, sm: 80 },
                                                        }}
                                                    >
                                                        <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveUser(member);
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                color: '#ef4444',
                                                                '&:hover': {
                                                                    backgroundColor:
                                                                        'rgba(239, 68, 68, 0.1)',
                                                                },
                                                                p: { xs: 0.5, sm: 1 },
                                                            }}
                                                            aria-label="Remove user"
                                                        >
                                                            <DeleteIcon
                                                                sx={{
                                                                    fontSize: { xs: 16, sm: 20 },
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Grid2>
            </Grid2>

            {/* Add User Modal */}
            <AddUserModal
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onUserAdded={handleUserAdded}
                projectId={projectId}
                existingMemberUserIds={members.map((member) => member.userId)}
            />

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ManageUsers;
